import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom-v5-compat'

import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ClearIcon from '@mui/icons-material/Clear'
import DescriptionIcon from '@mui/icons-material/Description'
import GetAppIcon from '@mui/icons-material/GetApp'
import SendIcon from '@mui/icons-material/Send'
import Autocomplete from '@mui/material/Autocomplete'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import InputAdornment from '@mui/material/InputAdornment'
import LinearProgress from '@mui/material/LinearProgress'
import ListItemIcon from '@mui/material/ListItemIcon'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Switch from '@mui/material/Switch'
import TextField from '@mui/material/TextField'
import Toolbar from '@mui/material/Toolbar'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { lighten, styled } from '@mui/material/styles'

import { getPath } from '~/utils'
import DateSelectButton from '../../oakra/components/date-select-button'
import FilterSelect from '../../oakra/components/filter-select'
import Select from '../../oakra/components/select'

const ORDER_BY_OPTIONS = [
    { value: '-date_created', label: gettext('Latest first') },
    { value: 'date_created', label: gettext('Earliest first') },
    { value: '-ship_by_date', label: gettext('Latest first - Ship By') },
    { value: 'ship_by_date', label: gettext('Earlier first - Ship By') },
]

const SKU_SEARCH_OPTIONS = [
    { value: 'contains', label: 'Contains SKU' },
    { value: 'single_sku_single_unit', label: gettext('1 of SKU') },
    { value: 'single_sku_two_units', label: gettext('2 of SKU') },
    { value: 'single_sku_three_units', label: gettext('3 of SKU') },
    { value: 'single_sku_four_units', label: gettext('4 of SKU') },
    { value: 'single_sku_five_units', label: gettext('5 of SKU') },
    { value: 'single_sku_more_than_five_units', label: gettext('>5 of SKU') },
]

const SingleSKU = styled('span')(({ theme, active = false }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 600,
    color: active ? theme.palette.secondary.contrastText : theme.palette.grey[600],
    height: 20,
    width: 20,
    borderRadius: 10,
    backgroundColor: active ? theme.palette.secondary.main : theme.palette.grey[300],
}))

function SkuSearchMenu({ onChange }) {
    const [anchorEl, setAnchorEl] = useState(null)
    const [selectedOption, setSelectedOption] = useState('contains')

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget)
    }

    const handleItemClick = (selectedOption) => {
        setSelectedOption(selectedOption)
        setAnchorEl(null)
        onChange(selectedOption)
    }

    const handleClose = () => {
        setAnchorEl(null)
    }

    return (
        <div>
            <IconButton
                aria-label="more"
                aria-controls="sku-search-menu"
                aria-haspopup="true"
                onClick={handleClick}
            >
                {selectedOption === 'single_sku_single_unit' && <SingleSKU active>1</SingleSKU>}
                {selectedOption === 'single_sku_two_units' && <SingleSKU active>2</SingleSKU>}
                {selectedOption === 'single_sku_three_units' && <SingleSKU active>3</SingleSKU>}
                {selectedOption === 'single_sku_four_units' && <SingleSKU active>4</SingleSKU>}
                {selectedOption === 'single_sku_five_units' && <SingleSKU active>5</SingleSKU>}
                {selectedOption === 'single_sku_more_than_five_units' && (
                    <SingleSKU active>+</SingleSKU>
                )}
                {selectedOption === 'contains' && <SingleSKU>1</SingleSKU>}
            </IconButton>
            <Menu
                id="sku-search-menu"
                anchorEl={anchorEl}
                keepMounted
                open={!!anchorEl}
                slotProps={{
                    paper: {
                        style: {
                            width: '20ch',
                        },
                    },
                }}
                onClose={handleClose}
            >
                {SKU_SEARCH_OPTIONS.map((option) => (
                    <MenuItem
                        key={option.value}
                        selected={selectedOption === option.value}
                        sx={{ fontSize: 12 }}
                        onClick={() => handleItemClick(option.value)}
                    >
                        {option.label}
                    </MenuItem>
                ))}
            </Menu>
        </div>
    )
}

export default function SalesTableToolbar({
    loading,
    numSelected,
    numTotal,
    shipSelectedSalesEnabled,
    processInsufficientStockSalesEnabled,
    shipAllCurrentFilterEnabled,
    filterOptions,
    filterValues,
    fulfillmentView,
    handleShipSelected,
    handleExportSelected,
    handleGenerateInvoicesForSelected,
    handleProcessInsufficientStock,
    handleFilterValuesChange,
    handleExportSales,
    handleOpenShippingInfoManager,
    handleDateTimeChange,
    handleSort,
    handleFilterChange,
    handleClearFilters,
    onFulfillmentViewChange,
}) {
    const [selectActionAnchorEl, setSelectActionAnchorEl] = useState(null)
    const [dateButtonText, setDateButtonText] = useState(gettext('Past 2 weeks'))
    const [orderBy, setOrderBy] = useState('-date_created')

    function handleOpenSelectAction(event) {
        setSelectActionAnchorEl(event.currentTarget)
    }

    function handleCloseSelectAction() {
        setSelectActionAnchorEl(null)
    }

    function handleDateChange(dateStart, dateEnd, buttonText) {
        handleDateTimeChange(dateStart, dateEnd)
        setDateButtonText(buttonText)
    }

    const skuFilterOption = useMemo(
        () => filterOptions.find((o) => o.lookup === 'sku'),
        [filterOptions]
    )
    const displayFilterOptions = useMemo(
        () => filterOptions.filter((o) => o.lookup !== 'sku'),
        [filterOptions]
    )

    const disableOtherFilterOptions = !!filterValues.id_value || !!filterValues.fulfillment_tracking

    return (
        <Toolbar
            sx={{
                paddingRight: (theme) => theme.spacing(1),
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                minHeight: 68,
                zIndex: 3,
                color: (theme) => (numSelected > 0 ? theme.palette.secondary.main : 'inherit'),
                backgroundColor: (theme) =>
                    numSelected > 0 ? lighten(theme.palette.secondary.light, 0.5) : 'inherit',
            }}
        >
            {loading && (
                <LinearProgress
                    color="secondary"
                    sx={{ position: 'absolute', left: 0, right: 0, top: 0 }}
                />
            )}
            <Box sx={{ flex: '0 1 auto', flexWrap: 'wrap' }}>
                {numSelected > 0 ? (
                    <Typography color="inherit" variant="subtitle1">
                        {interpolate(pgettext('Number selected', '%s selected'), [numSelected])}
                    </Typography>
                ) : (
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'center',
                            my: 1,
                            mr: 1.25,
                        }}
                    >
                        <DateSelectButton
                            startDate={filterValues.date_created_after}
                            endDate={filterValues.date_created_before}
                            handleDateChange={handleDateChange}
                            initialButtonText={dateButtonText}
                            disabled={disableOtherFilterOptions}
                        />
                        <TextField
                            id="id_value"
                            type="search"
                            sx={{ width: 150, my: 0.25, mx: 1 }}
                            value={filterValues.id_value}
                            margin="normal"
                            variant="outlined"
                            placeholder={gettext('Order ID')}
                            InputProps={{
                                style: { height: 30, fontSize: 13 },
                            }}
                            inputProps={{ style: { paddingLeft: 9 } }}
                            onChange={(e) =>
                                handleFilterValuesChange({
                                    id_value: e.target.value,
                                    fulfillment_tracking: '',
                                })
                            }
                        />

                        <TextField
                            id="fulfillment_tracking"
                            type="search"
                            sx={{ width: 150, my: 0.25, mx: 1, marginLeft: 0 }}
                            value={filterValues.fulfillment_tracking}
                            margin="normal"
                            variant="outlined"
                            placeholder={gettext('Tracking, Fulfillment ID')}
                            InputProps={{
                                style: { height: 30, fontSize: 13 },
                            }}
                            inputProps={{ style: { paddingLeft: 9 } }}
                            onChange={(e) =>
                                handleFilterValuesChange({
                                    fulfillment_tracking: e.target.value,
                                    id_value: '',
                                })
                            }
                        />
                        <Autocomplete
                            id="sku-search"
                            freeSolo
                            ListboxProps={{
                                sx: {
                                    fontSize: 12,
                                },
                            }}
                            options={skuFilterOption?.values || []}
                            renderOption={(props, option) => {
                                return <li {...props}>{option}</li>
                            }}
                            renderInput={(params) => {
                                return (
                                    <TextField
                                        {...params}
                                        id="sale_line_items__sku"
                                        size="small"
                                        margin="normal"
                                        variant="outlined"
                                        sx={{ width: 150, my: 0.25, mr: 1 }}
                                        value={filterValues.sale_line_items__sku}
                                        disabled={disableOtherFilterOptions}
                                        placeholder={gettext('SKU')}
                                        InputProps={{
                                            ...params.InputProps,
                                            style: { padding: '3px 0px 3px 3px', fontSize: 13 },
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <SkuSearchMenu
                                                        onChange={(value) => {
                                                            handleFilterValuesChange({
                                                                sku_search_type: value,
                                                            })
                                                        }}
                                                    />
                                                </InputAdornment>
                                            ),
                                        }}
                                        onChange={(e) => {
                                            handleFilterValuesChange({
                                                sale_line_items__sku: e.target.value,
                                            })
                                        }}
                                    />
                                )
                            }}
                            onChange={(_e, value, _reason) => {
                                handleFilterValuesChange({
                                    sale_line_items__sku: value,
                                })
                            }}
                        />

                        <TextField
                            id="search"
                            type="search"
                            sx={{ width: 150, my: 0.25, mr: 1 }}
                            variant="outlined"
                            margin="normal"
                            value={filterValues.search}
                            disabled={disableOtherFilterOptions}
                            onChange={(e) => handleFilterValuesChange({ search: e.target.value })}
                            placeholder={gettext('Product Name')}
                            InputProps={{
                                style: { height: 30, fontSize: 13 },
                            }}
                            inputProps={{ style: { paddingLeft: 9 } }}
                        />
                        {displayFilterOptions.map((field) => (
                            <FilterSelect
                                key={field.lookup}
                                field={field}
                                disabled={disableOtherFilterOptions}
                                value={filterValues[field.lookup]}
                                handleFilterChange={handleFilterChange}
                            />
                        ))}

                        <Tooltip title={gettext('Clear filters')}>
                            <IconButton
                                color="default"
                                sx={{ fontSize: 13, ml: 1, minWidth: 25 }}
                                onClick={handleClearFilters}
                            >
                                <ClearIcon />
                            </IconButton>
                        </Tooltip>

                        {numTotal > 0 && (
                            <Typography
                                variant="caption"
                                sx={{ color: (theme) => theme.palette.text.secondary }}
                            >
                                {interpolate(gettext('%s orders'), [numTotal])}
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>
            <Box
                sx={{
                    color: (theme) => theme.palette.text.secondary,
                    display: 'flex',
                    flex: '0 0 auto',
                }}
            >
                {numSelected > 0 ? (
                    <>
                        {processInsufficientStockSalesEnabled && (
                            <Button color="secondary" onClick={handleProcessInsufficientStock}>
                                {gettext('Change status to pending')}
                            </Button>
                        )}
                        <Button color="secondary" onClick={handleGenerateInvoicesForSelected}>
                            {gettext('Generate tax invoices')}
                        </Button>
                        <Button color="secondary" onClick={handleExportSelected}>
                            {gettext('Export selected')}
                        </Button>
                        <Button
                            color="secondary"
                            onClick={handleShipSelected}
                            disabled={!shipSelectedSalesEnabled}
                        >
                            {gettext('Ship selected')}
                        </Button>
                    </>
                ) : (
                    <>
                        <div style={{ display: 'block', textAlign: 'right', padding: 10 }}>
                            <Button
                                variant="outlined"
                                color="primary"
                                sx={{ alignSelf: 'center', flexShrink: 0 }}
                                aria-owns={selectActionAnchorEl ? 'actions-menu' : undefined}
                                aria-haspopup="true"
                                onClick={handleOpenSelectAction}
                            >
                                {gettext('Actions')}
                                <ArrowDropDownIcon />
                            </Button>

                            <Select
                                key="sortBy"
                                defaultValue="-date_created"
                                containerStyle={{ marginRight: 0, marginTop: 5 }}
                                width={175}
                                isMulti={false}
                                isClearable={false}
                                options={ORDER_BY_OPTIONS}
                                value={ORDER_BY_OPTIONS.find((o) => o.value === orderBy)}
                                placeholder={gettext('Order by')}
                                onChange={(option) => {
                                    setOrderBy(option.value)
                                    handleSort(orderBy)
                                }}
                            />

                            <Tooltip title={gettext('Switch to fulfillments view')}>
                                <div>
                                    <Typography style={{ fontSize: 13 }} component="span">
                                        {gettext('Show fulfillments')}
                                    </Typography>
                                    <Switch
                                        color="secondary"
                                        checked={fulfillmentView}
                                        onChange={(_, checked) => onFulfillmentViewChange(checked)}
                                    />
                                </div>
                            </Tooltip>
                        </div>

                        <Menu
                            id="actions-menu"
                            anchorEl={selectActionAnchorEl}
                            open={!!selectActionAnchorEl}
                            onClose={handleCloseSelectAction}
                        >
                            <MenuItem
                                disabled={!shipAllCurrentFilterEnabled}
                                onClick={() => {
                                    setSelectActionAnchorEl(null)
                                    handleOpenShippingInfoManager()
                                }}
                            >
                                <ListItemIcon>
                                    <SendIcon />
                                </ListItemIcon>
                                <ListItemText primary={gettext('Ship current filter orders')} />
                            </MenuItem>
                            <MenuItem
                                onClick={() => {
                                    setSelectActionAnchorEl(null)
                                    handleExportSales()
                                }}
                            >
                                <ListItemIcon>
                                    <GetAppIcon />
                                </ListItemIcon>
                                <ListItemText primary={gettext('Export sales')} />
                            </MenuItem>
                            <MenuItem component={Link} to={getPath('sale', { sale_id: 'new' })}>
                                <ListItemIcon>
                                    <DescriptionIcon />
                                </ListItemIcon>
                                <ListItemText primary={gettext('New sale')} />
                            </MenuItem>
                        </Menu>
                    </>
                )}
            </Box>
        </Toolbar>
    )
}
